import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { logger } from '@/lib/logger'
import { useAdmin } from './use-admin'
import { formatPhone } from '@/lib/utils'
import type { FormEvent } from 'react'
import type { TipoPermissao } from './use-admin'

export interface EditarUsuarioForm {
  nome: string
  email: string
  telefone: string
  endereco: string
  igreja_id: string
  permissao: TipoPermissao
  is_admin: boolean
  ativo: boolean
}

export function useEditarUsuario() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { usuarios, igrejas, updateUsuario, isUpdatingUsuario } = useAdmin()
  
  const [formState, setFormState] = useState<EditarUsuarioForm>({
    nome: '',
    email: '',
    telefone: '',
    endereco: '',
    igreja_id: '',
    permissao: 'membro',
    is_admin: false,
    ativo: true
  })

  const [errors, setErrors] = useState<Partial<Record<keyof EditarUsuarioForm, string>>>({})
  const [isLoading, setIsLoading] = useState(true)

  // Carregar dados do usuário quando o componente montar
  useEffect(() => {
    if (id && usuarios.length > 0) {
      const usuario = usuarios.find(u => u.id === id)
      if (usuario) {
        setFormState({
          nome: usuario.nome || '',
          email: usuario.email || '',
          telefone: usuario.telefone || '',
          endereco: usuario.endereco || '',
          igreja_id: usuario.igreja_id || '',
          permissao: (usuario.permissao as TipoPermissao) || 'membro',
          is_admin: usuario.is_admin || false,
          ativo: usuario.ativo ?? true
        })
        setIsLoading(false)
      } else {
        toast.error('Usuário não encontrado')
        navigate('/admin-app')
      }
    }
  }, [id, usuarios, navigate])

  const handleInputChange = (field: keyof EditarUsuarioForm, value: string | boolean | TipoPermissao) => {
    let processedValue = value
    
    // Apply phone mask if field is telefone
    if (field === 'telefone' && typeof value === 'string') {
      processedValue = formatPhone(value)
    }
    
    setFormState(prev => ({ ...prev, [field]: processedValue }))
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof EditarUsuarioForm, string>> = {}

    if (!formState.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório'
    }

    if (!formState.email.trim()) {
      newErrors.email = 'Email é obrigatório'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email)) {
      newErrors.email = 'Email inválido'
    }

    if (!formState.igreja_id) {
      newErrors.igreja_id = 'Igreja é obrigatória'
    }

    if (!formState.permissao) {
      newErrors.permissao = 'Permissão é obrigatória'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Por favor, corrija os erros no formulário')
      return
    }

    if (!id) {
      toast.error('ID do usuário não encontrado')
      return
    }

    try {
      logger.info('Atualizando usuário', { context: 'EDITAR_USUARIO' })

      await updateUsuario({
        id,
        ...formState
      })

      toast.success('Usuário atualizado com sucesso!')
      navigate('/admin-app')
    } catch (error) {
      logger.error('Erro ao atualizar usuário', { context: 'EDITAR_USUARIO' })
      console.error('Detalhes do erro:', error)
      toast.error('Erro ao atualizar usuário. Tente novamente.')
    }
  }

  const handleCancel = () => {
    navigate('/admin-app')
  }

  return {
    formState,
    errors,
    isLoading,
    isSubmitting: isUpdatingUsuario,
    igrejas,
    handleInputChange,
    handleSubmit,
    handleCancel
  }
}